// Shared validator for the public configuration (FR-036, FR-037; data-model §10; research R14).
// Used by app.config.ts at build/start and by src/config at app start. Hand-written on purpose (no zod).

export const APP_ENVS = ["local", "staging", "production"] as const;
export type AppEnv = (typeof APP_ENVS)[number];

export type PublicConfig = {
  appEnv: AppEnv;
  /** scheme + host [+ port], no path, no trailing slash; https unless local */
  apiBaseUrl: string;
  /** same rules as apiBaseUrl */
  webBaseUrl: string;
  /** 002 FR-009: the terms of use page (https). The sign-up terms line shows only with both. */
  termsUrl?: string;
  /** 002 FR-009: the privacy policy page (https). */
  privacyUrl?: string;
  /**
   * 003 FR-035: SHA-256 signing-certificate fingerprints for Android App Links (assetlinks.json).
   * Public values, not secrets. Each is 32 upper-case hex pairs joined by colons.
   */
  androidCertFingerprints?: string[];
  /** 004 (data-model §9; research R3): the pin map provider and tiles. */
  map: MapConfig;
};

export type MapConfig = {
  /** Only the OpenStreetMap/Leaflet provider is built (decision 2026-10-06 "Maps"). */
  provider: "osm";
  /** Tile template with {z}, {x}, {y}; https unless appEnv is local. */
  tileUrl: string;
  attribution: string;
  defaultCenter: { latitude: number; longitude: number; zoom: number };
  /** Place search / reverse geocoding: none until a provider is configured. */
  geocoder: "none";
};

/** A configuration failure that names the setting at fault. */
export class ConfigError extends Error {
  readonly setting: string;

  constructor(setting: string, message: string) {
    super(message);
    this.name = "ConfigError";
    this.setting = setting;
  }
}

const ENV_LIST = APP_ENVS.join(" | ");
// scheme://host[:port] and nothing else.
const BASE_URL =
  /^(https?):\/\/([a-z0-9]([a-z0-9-]*[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*(:\d{1,5})?$/i;

function isAppEnv(value: unknown): value is AppEnv {
  return (
    typeof value === "string" && (APP_ENVS as readonly string[]).includes(value)
  );
}

/** Parses the build-time APP_ENV variable. */
export function parseAppEnv(value: unknown): AppEnv {
  if (value === undefined || value === null || value === "") {
    throw new ConfigError("APP_ENV", `APP_ENV is required (${ENV_LIST})`);
  }
  if (!isAppEnv(value)) {
    throw new ConfigError(
      "APP_ENV",
      `APP_ENV must be one of ${ENV_LIST} (got "${String(value)}")`,
    );
  }
  return value;
}

function baseUrl(
  raw: Record<string, unknown>,
  key: "apiBaseUrl" | "webBaseUrl",
  appEnv: AppEnv,
): string {
  const value = raw[key];
  if (typeof value !== "string" || value === "") {
    throw new ConfigError(key, `${key} is required`);
  }
  const match = BASE_URL.exec(value);
  if (!match) {
    throw new ConfigError(
      key,
      `${key} must be a scheme and host with an optional port, no path and no trailing slash (got "${value}")`,
    );
  }
  if (appEnv !== "local" && match[1]?.toLowerCase() !== "https") {
    throw new ConfigError(
      key,
      `${key} must use https outside local (got "${value}")`,
    );
  }
  return value;
}

// https://host.tld[/path…], no spaces.
const DOCUMENT_URL = /^https:\/\/[^\s/?#]+\.[^\s/?#]+(\/\S*)?$/i;

/** An optional https address of a document Akaiva publishes (002 FR-009). */
function documentUrl(
  raw: Record<string, unknown>,
  key: "termsUrl" | "privacyUrl",
): string | undefined {
  const value = raw[key];
  if (value === undefined) return undefined;
  if (typeof value !== "string" || !DOCUMENT_URL.test(value)) {
    throw new ConfigError(key, `${key} must be an https URL`);
  }
  return value;
}

export const CERT_FINGERPRINT = /^([0-9A-F]{2}:){31}[0-9A-F]{2}$/;

/** 003 FR-035: optional list of signing-certificate fingerprints. */
function fingerprints(raw: Record<string, unknown>): string[] | undefined {
  const value = raw.androidCertFingerprints;
  if (value === undefined) return undefined;
  if (
    !Array.isArray(value) ||
    !value.every((v) => typeof v === "string" && CERT_FINGERPRINT.test(v))
  ) {
    throw new ConfigError(
      "androidCertFingerprints",
      "androidCertFingerprints must be a list of SHA-256 fingerprints (AA:BB:…, 32 pairs)",
    );
  }
  return value as string[];
}

const isRecordValue = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const inRange = (v: unknown, min: number, max: number): v is number =>
  typeof v === "number" && Number.isFinite(v) && v >= min && v <= max;

/** 004 FR-028, FR-031, SC-005: the map block (data-model §9). */
function mapConfig(raw: Record<string, unknown>, appEnv: AppEnv): MapConfig {
  const map = raw.map;
  if (!isRecordValue(map)) throw new ConfigError("map", "map is required");
  if (map.provider === "google") {
    throw new ConfigError(
      "map.provider",
      "map.provider: the google map provider is not built yet",
    );
  }
  if (map.provider !== "osm") {
    throw new ConfigError("map.provider", 'map.provider must be "osm"');
  }
  const tileUrl = map.tileUrl;
  if (
    typeof tileUrl !== "string" ||
    !["{z}", "{x}", "{y}"].every((p) => tileUrl.includes(p)) ||
    !/^https?:\/\/\S+$/i.test(tileUrl) ||
    (appEnv !== "local" && !tileUrl.toLowerCase().startsWith("https://"))
  ) {
    throw new ConfigError(
      "map.tileUrl",
      "map.tileUrl must be an http(s) template with {z}, {x} and {y} (https outside local)",
    );
  }
  if (typeof map.attribution !== "string" || map.attribution.trim() === "") {
    throw new ConfigError("map.attribution", "map.attribution is required");
  }
  const c = map.defaultCenter;
  if (
    !isRecordValue(c) ||
    !inRange(c.latitude, 6, 37.6) ||
    !inRange(c.longitude, 68, 97.5) ||
    !inRange(c.zoom, 3, 19)
  ) {
    throw new ConfigError(
      "map.defaultCenter",
      "map.defaultCenter needs latitude 6–37.6, longitude 68–97.5 and zoom 3–19",
    );
  }
  if (map.geocoder !== "none") {
    throw new ConfigError("map.geocoder", 'map.geocoder must be "none"');
  }
  return {
    provider: "osm",
    tileUrl,
    attribution: map.attribution,
    defaultCenter: {
      latitude: c.latitude,
      longitude: c.longitude,
      zoom: c.zoom,
    },
    geocoder: "none",
  };
}

/** Validates an untrusted value and returns a typed PublicConfig, or throws a ConfigError. */
export function validatePublicConfig(raw: unknown): PublicConfig {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    throw new ConfigError(
      "publicConfig",
      "publicConfig is missing or is not an object",
    );
  }
  const record = raw as Record<string, unknown>;
  if (!isAppEnv(record.appEnv)) {
    throw new ConfigError("appEnv", `appEnv must be one of ${ENV_LIST}`);
  }
  const appEnv = record.appEnv;
  const termsUrl = documentUrl(record, "termsUrl");
  const privacyUrl = documentUrl(record, "privacyUrl");
  const androidCertFingerprints = fingerprints(record);
  const map = mapConfig(record, appEnv);
  return {
    appEnv,
    apiBaseUrl: baseUrl(record, "apiBaseUrl", appEnv),
    webBaseUrl: baseUrl(record, "webBaseUrl", appEnv),
    ...(termsUrl === undefined ? {} : { termsUrl }),
    ...(privacyUrl === undefined ? {} : { privacyUrl }),
    ...(androidCertFingerprints === undefined
      ? {}
      : { androidCertFingerprints }),
    map,
  };
}
